import React, { Fragment, useCallback } from "react";
import { Row, Col, Button } from "reactstrap";
import { FieldArray } from "react-final-form-arrays";
import { useForm } from "react-final-form";
import _ from "lodash";

import RowComponent from "./3_Cols";

import "./FormBuilder.css";

const liveCols = (cols) => _.filter(cols, (c) => !c?._destroy);

const RowsAttributes = (props) => {
  const { fields, column_types, disabled, revisionIndex, custom_form } = props;
  const form = useForm();

  const addRow = () => {
    fields.push({});
  };

  const lastRow = fields.value ? fields.value[fields.length - 1] : null;
  let lastRowCols = null;
  if (lastRow) {
    lastRowCols = liveCols(lastRow?.cols_attributes);
  }

  const removeRow = (i) => {
    const thisField = fields.value[i];
    if (thisField?.id) {
      fields.update(i, { id: thisField.id, _destroy: "1" });
    } else {
      fields.remove(i);
    }
  };

  // Atomic rewrite of rows_attributes — avoids multi-step index drift
  const writeRows = useCallback(
    (nextRows) => {
      form.change(fields.name, nextRows);
    },
    [form, fields.name]
  );

  // Move a col onto another position; returns the item's new { rowIndex, colIndex } or null.
  // Called live from drag hover, so moves happen as you drag.
  const moveCol = useCallback(
    (fromRowIndex, fromColIndex, toRowIndex, toColIndex) => {
      if (fromRowIndex === toRowIndex && fromColIndex === toColIndex) return null;

      const rows = _.cloneDeep(fields.value || []);
      const fromRow = rows[fromRowIndex];
      if (!fromRow?.cols_attributes?.[fromColIndex]) return null;

      const [moved] = fromRow.cols_attributes.splice(fromColIndex, 1);

      if (fromRowIndex === toRowIndex) {
        // Take the hovered col's spot (sortable semantics)
        fromRow.cols_attributes.splice(toColIndex, 0, moved);
        writeRows(rows);
        return { rowIndex: fromRowIndex, colIndex: toColIndex };
      }

      const toRow = rows[toRowIndex];
      if (!toRow) return null;
      toRow.cols_attributes = toRow.cols_attributes || [];

      const toWidth = _.sumBy(liveCols(toRow.cols_attributes), (c) => parseInt(c.desktop_width) || 0) || 0;
      const movedWidth = parseInt(moved.desktop_width) || 0;
      if (toWidth + movedWidth > 12) return null;

      const insertAt = Math.min(toColIndex, toRow.cols_attributes.length);
      toRow.cols_attributes.splice(insertAt, 0, moved);

      let newRowIndex = toRowIndex;
      if (_.isEmpty(liveCols(fromRow.cols_attributes))) {
        if (fromRow.id) {
          rows[fromRowIndex] = { id: fromRow.id, _destroy: "1" };
        } else {
          rows.splice(fromRowIndex, 1);
          if (fromRowIndex < toRowIndex) newRowIndex -= 1;
        }
      }

      writeRows(rows);
      return { rowIndex: newRowIndex, colIndex: insertAt };
    },
    [fields.value, writeRows]
  );

  // Pull a col out into its own new row at atRowIndex; returns new row index or null.
  const insertColAsRow = useCallback(
    (fromRowIndex, fromColIndex, atRowIndex) => {
      const rows = _.cloneDeep(fields.value || []);
      const fromRow = rows[fromRowIndex];
      if (!fromRow?.cols_attributes?.[fromColIndex]) return null;

      const [moved] = fromRow.cols_attributes.splice(fromColIndex, 1);
      const alone = _.isEmpty(liveCols(fromRow.cols_attributes));

      // Alone in row + adjacent slot = already there
      if (alone && (atRowIndex === fromRowIndex || atRowIndex === fromRowIndex + 1)) {
        return null;
      }

      let insertAt = atRowIndex;

      if (alone && !fromRow.id) {
        rows.splice(fromRowIndex, 1);
        if (insertAt > fromRowIndex) insertAt -= 1;
      } else if (alone && fromRow.id) {
        rows[fromRowIndex] = { id: fromRow.id, _destroy: "1" };
      }

      rows.splice(insertAt, 0, { cols_attributes: [moved] });
      writeRows(rows);
      return insertAt;
    },
    [fields.value, writeRows]
  );

  const canDropColOnRow = useCallback(
    (item, toRowIndex) => {
      if (!item) return false;
      if (item.rowIndex === toRowIndex) return true;
      const toRow = fields.value?.[toRowIndex];
      const toWidth = _.sumBy(liveCols(toRow?.cols_attributes), (c) => parseInt(c.desktop_width) || 0) || 0;
      return toWidth + (parseInt(item.desktop_width) || 0) <= 12;
    },
    [fields.value]
  );

  return (
    <Row>
      <Col md={12}>
        {fields.map((field, rowIndex) => {
          const thisField = fields.value[rowIndex];
          const markedForDestruction = _.get(thisField, "_destroy");

          const canGoUp = rowIndex > 0;
          const canGoDown = rowIndex < fields.length - 1;

          const moveDown = () => {
            fields.swap(rowIndex, rowIndex + 1);
          };

          const moveUp = () => {
            fields.swap(rowIndex, rowIndex - 1);
          };

          if (!markedForDestruction) {
            return (
              <Fragment key={rowIndex}>
                <hr />
                <FieldArray
                  key={rowIndex}
                  component={RowComponent}
                  name={`${field}.cols_attributes`}
                  subscription={{ length: true, value: true }}
                  column_types={column_types}
                  disabled={disabled}
                  canGoDown={canGoDown}
                  canGoUp={canGoUp}
                  revisionIndex={revisionIndex}
                  moveDown={moveDown}
                  moveUp={moveUp}
                  custom_form={custom_form}
                  rowIndex={rowIndex}
                  moveCol={moveCol}
                  insertColAsRow={insertColAsRow}
                  canDropColOnRow={canDropColOnRow}
                />
              </Fragment>
            );
          }
        })}

        <hr />
        {!disabled && (
          <Row className="mt-2">
            <Col>
              <Button color="success" size="sm" onClick={addRow}>
                Add Row
              </Button>
              {fields.length > 0 && _.isEmpty(lastRowCols) && (
                <Button
                  className="ml-2"
                  size="sm"
                  onClick={() => {
                    removeRow(fields.length - 1);
                  }}
                >
                  Remove Last Row
                </Button>
              )}
            </Col>
          </Row>
        )}
      </Col>
    </Row>
  );
};

export default RowsAttributes;
